import { Instagram, Facebook, Phone, MessageCircle } from "lucide-react";
import logoImage from "@assets/Logo design (7)_1760256523607_opt.png";
import EmailSignup from "./EmailSignup";
import { CookieSettingsLink } from "./ConsentBanner";

export default function Footer() {
  return (
    <footer className="bg-background border-t border-border py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="sm:col-span-2 lg:col-span-2 min-w-0">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-16 rounded-lg shadow-sm border-2 border-[#cbb26b]/30 overflow-hidden">
                <img 
                  src={logoImage} 
                  alt="Effortless Gym Logo" 
                  className="w-full h-full object-contain"
                  width={64}
                  height={64}
                />
              </div>
              <span className="font-alata text-xl font-bold gradient-text">Effortless Gym</span>
            </div>
            <p className="text-muted-foreground mb-6 max-w-md">
              Effortless Gym is the alternative to crowded commercial gyms. Premium training spaces in Bournemouth and Poole with deliberately limited capacity and 24/7 member access.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/effortlessgyms/" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-card border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid="link-instagram"
                aria-label="Effortless Gym on Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61580077131520" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-card border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                data-testid="link-facebook"
                aria-label="Effortless Gym on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3 text-muted-foreground">
              <li><a href="/gym-bournemouth-airport" className="hover:text-primary transition-colors" data-testid="footer-link-bournemouth-airport">Bournemouth Airport</a></li>
              <li><a href="/poole" className="hover:text-primary transition-colors" data-testid="footer-link-poole">Poole</a></li>
              <li><a href="/gym-bournemouth" className="hover:text-primary transition-colors" data-testid="footer-link-gym-bournemouth">Gym in Bournemouth</a></li>
              <li><a href="/hyrox-gym-poole" className="hover:text-primary transition-colors" data-testid="footer-link-gym-poole">HYROX Gym in Poole</a></li>
              <li><a href="/membership" className="hover:text-primary transition-colors" data-testid="footer-link-membership">Membership Prices</a></li>
              <li><a href="/how-it-works" className="hover:text-primary transition-colors" data-testid="footer-link-how-it-works">How It Works</a></li>
              <li><a href="/booking" className="hover:text-primary transition-colors" data-testid="footer-link-booking">Visitor Booking</a></li>
              <li><a href="/business-use" className="hover:text-primary transition-colors" data-testid="footer-link-business-use">PT & Business Bookings</a></li>
              <li><a href="/contact" className="hover:text-primary transition-colors" data-testid="footer-link-contact">Contact Us</a></li>
            </ul>
          </div>

          <div>
            <a href="/contact" className="inline-block" data-testid="footer-link-contact-heading">
              <h3 className="font-semibold mb-6 hover:text-primary transition-colors cursor-pointer">Contact</h3>
            </a>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <a 
                  href="tel:+447355495471" 
                  className="hover:text-primary transition-colors flex items-center gap-2"
                  data-testid="link-phone"
                >
                  <Phone className="w-4 h-4" />
                  +44 7355 495471
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/447355495471" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors flex items-center gap-2"
                  data-testid="link-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp Us
                </a>
              </li>
              <li>
                <a 
                  href="mailto:booking@effortlessgym.co.uk"
                  className="hover:text-primary transition-colors"
                  data-testid="link-email"
                >
                  booking@effortlessgym.co.uk
                </a>
              </li>
              <li data-testid="text-location-bournemouth">
                <span className="text-foreground font-medium">Bournemouth Airport</span><br />
                76 Basepoint Business Centre<br />Aviation Park West, Hurn<br />Christchurch, England<br />BH23 6NX
              </li>
              <li data-testid="text-location-poole">
                <span className="text-foreground font-medium">Poole</span><br />
                Unit D2, Arena Business Centre<br />Holyrood Close<br />Poole, England<br />BH17 7FJ
              </li>
              <li data-testid="text-opening">Both Sites Now Open</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-8 mt-12">
          <div className="flex flex-col lg:flex-row gap-8 items-center mb-8">
            <EmailSignup />
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm">
              &copy; 2026 Effortless Gym. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <CookieSettingsLink />
              <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors text-sm" data-testid="footer-link-privacy">
                Privacy Policy
              </a>
              <a href="/safety-documents" className="text-muted-foreground hover:text-foreground transition-colors text-xs" data-testid="footer-link-safety-documents">
                Safety Data Sheets
              </a>
            </div>
            <p className="text-muted-foreground text-sm">
              Proud to serve Bournemouth, Poole, Christchurch and surrounding areas
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
